// F1 Pit Stops campaign (User Story 8582). Assets live on the CDN under /f1
// preserving the delivery folder structure (MOBILE/, MOBILE/HOME/, VIDEO/).
// File names contain spaces and parentheses, so every URL built from these
// constants must go through encodeURI.
export const F1_ASSETS_BASE = `${AZURE_BASE_URL}/f1`
export const F1_MOBILE_ASSETS = `${F1_ASSETS_BASE}/MOBILE`
export const F1_TABLET_ASSETS = `${F1_ASSETS_BASE}/TABLET`
export const F1_DESKTOP_ASSETS = `${F1_ASSETS_BASE}/DESKTOP`
export const F1_HOME_ASSETS = `${F1_MOBILE_ASSETS}/HOME`
export const F1_HOME_TABLET_ASSETS = `${F1_TABLET_ASSETS}/HOME`
export const F1_HOME_DESKTOP_ASSETS = `${F1_DESKTOP_ASSETS}/HOME`

// Breakpoint sources for campaign art: every asset ships as
// <name>-mobile/-tablet/-desktop under its breakpoint folder.
export const f1AssetSources = (name: string) => ({
  mobile: encodeURI(`${F1_MOBILE_ASSETS}/${name}-mobile.webp`),
  tablet: encodeURI(`${F1_TABLET_ASSETS}/${name}-tablet.webp`),
  desktop: encodeURI(`${F1_DESKTOP_ASSETS}/${name}-desktop.webp`)
})

// Raw (unencoded) on purpose: VideoBackground runs encodeURI on its src.
export const F1_BADGE_VIDEO = `${F1_ASSETS_BASE}/VIDEO/04 gafete 10 (1)_compressed.mp4`

// Backend ids of the weekly dynamics. TODO: fill with the real ids before
// each pit stop goes live; while an id is null its module renders as
// "not available" (never a broken link).
export const PIT_STOP_IDS = {
  triviaId: 22 as number | null,
  ticketAlbumId: 5 as number | null,
  photoAlbumId: null as number | null
}

export type PitStopKey = 'trivia' | 'ticket' | 'photo' | 'ranking'

export interface PitStop {
  key: PitStopKey
  order: number
  /** Module name as shown in the logged-in modules frame (5409:3581) */
  name: string
  /** Module description (logged-in frame copy) */
  description: string
  /** ISO date (Mexico City civil date) the pit stop unlocks */
  startsAt: string
  /** Date label shown in the Calendar section (Figma 5595:12134) */
  dateLabel: string
  /** Date label used by the disabled button ("Se habilita el ...") */
  unlockLabel: string
}

// Entry route of each dynamic (verified against src/routes/routes.tsx and
// src/views/album/configs/routes/definitions.ts):
// - trivia  -> /trivia/qr/:id (TriviaQr, PUBLIC): trivia 22 is DIRECT ACCESS
//   only — validate/false returns 400 code 513, validate/true returns 200.
// - ticket  -> /album/:id/upload (rally upload flow, /album is PRIVATE)
// - photo   -> /album/:id/upload
// - ranking -> none: Phase 5 renders the gallery on this landing itself.

// PENDING BACKEND: /album/:id/upload validates with directAccess=false (no
// ?qr query). If the campaign albums turn out to be direct-access like the
// trivia, set this to '?qr=true' — single switch for both rally routes.
// To test once the backend is back: open /album/5/upload and check the
// validate request ends in /true and returns 200.
const F1_ALBUM_QUERY = ''

export const getPitStopRoute = (key: PitStopKey): string | null => {
  switch (key) {
  case 'trivia':
    return PIT_STOP_IDS.triviaId !== null ? `/trivia/qr/${PIT_STOP_IDS.triviaId}` : null
  case 'ticket':
    return PIT_STOP_IDS.ticketAlbumId !== null
      ? `/album/${PIT_STOP_IDS.ticketAlbumId}/upload${F1_ALBUM_QUERY}`
      : null
  case 'photo':
    return PIT_STOP_IDS.photoAlbumId !== null
      ? `/album/${PIT_STOP_IDS.photoAlbumId}/upload${F1_ALBUM_QUERY}`
      : null
  default:
    return null
  }
}

export const PIT_STOPS: PitStop[] = [
  {
    key: 'trivia',
    order: 1,
    name: 'Trivia',
    description: 'Pon a prueba tus conocimientos sobre F1® y Heineken®.',
    startsAt: '2026-09-27',
    dateLabel: '28 de septiembre',
    unlockLabel: '28 de septiembre'
  },
  {
    key: 'ticket',
    order: 2,
    name: 'Registra tu ticket',
    description: 'Registra tu compra y continúa avanzando hacia la experiencia F1®.',
    startsAt: '2026-10-05',
    dateLabel: '5 de octubre',
    unlockLabel: '5 de octubre'
  },
  {
    key: 'photo',
    order: 3,
    name: 'Comparte tu foto',
    description: 'Sube una foto que capture cómo disfrutas Heineken®.',
    startsAt: '2026-10-12',
    dateLabel: '12 de octubre',
    unlockLabel: '12 de octubre'
  },
  {
    key: 'ranking',
    order: 4,
    name: 'Ranking de votos',
    description: 'Elige el momento Heineken® que merece llegar a la gran final.',
    startsAt: '2026-10-19',
    dateLabel: '19 al 25 de octubre',
    unlockLabel: '19 de octubre'
  }
]

// Anchor of the Pit Stops section (logged users get scrolled here by the CTAs)
export const PIT_STOPS_ANCHOR_ID = 'pit-stops'

// Dynamics onboarding art (components section of canvas 5404:6322)
export const F1_PH_ASSETS = `${F1_ASSETS_BASE}/PH`

// Instant-win card shown in the trivia win popup when the admin prize has
// no (or a broken) imageUrl — the CDN card bakes frame, art and legend.
export const F1_TRIVIA_PRIZE_FALLBACK_IMAGE = encodeURI(
  `${F1_PH_ASSETS}/Premios/trivia-asset_instant_win.webp`
)

export const F1_TICKET_PRIZE_FALLBACK_IMAGE = encodeURI(
  `${F1_PH_ASSETS}/Premios/subir_ticket-asset_instant_win.webp`
)

// Participation rules of the ticket rally (Figma 5561:7508). The sheet in
// the frame shows title + items; the intro line lives on the screen behind
// it and is kept here so the whole approved copy ships together.
export interface F1RulesContent {
  title: string
  intro: string
  items: string[]
}

export const F1_TICKET_RULES: F1RulesContent = {
  title: 'Reglas de participación',
  intro:
    'Suba una foto en donde se vea bien el detalle de tu compra. Solo se tomarán en cuenta productos Heineken.',
  items: [
    'El ticket debe ser de productos Heineken en alguna de sus presentaciones: Heineken Original Botella (6 pack y 12 pack), Heineken 0.0 Botella (6 pack) y Heineken 0.0 lata individual.',
    'Se debe visualizar la fecha de compra.',
    'Debe ser de un punto de venta (OXXO/WALMART).',
    'Debe ser de un mínimo de compra de $200 M.N. en productos Heineken.'
  ]
}

// Upload-validate codes that mean "ticket limit reached" for the F1 rally:
// only these keep the generic "¡Espera un poco!" wait screen. TODO backend:
// confirm the exact limit code once the service is back.
export const F1_TICKET_LIMIT_CODES: Array<number | string> = [1116]

export interface F1OnboardingSlide {
  image: string
  title: string
  text: string
}

// Slide 2 ("Completa los Pit Stops") is shared by the three dynamics
const SHARED_ONBOARDING_SLIDE: F1OnboardingSlide = {
  image: encodeURI(`${F1_PH_ASSETS}/asset_onbording-2.webp`),
  title: 'Completa los Pit Stops',
  text: 'Participa semanalmente para ganar uno de los 20 pases dobles a Foro Sol o el gran premio: un pase doble al Paddock de F1.'
}

export const F1_TRIVIA_ONBOARDING_SLIDES: F1OnboardingSlide[] = [
  {
    image: encodeURI(`${F1_PH_ASSETS}/trivia-asset_onbording-1.webp`),
    title: 'Responde la trivia',
    text: 'Pon a prueba tus conocimientos sobre F1® y Heineken®.'
  },
  SHARED_ONBOARDING_SLIDE
]

export const F1_TICKET_ONBOARDING_SLIDES: F1OnboardingSlide[] = [
  {
    image: encodeURI(`${F1_PH_ASSETS}/subir_ticket-asset_onbording-1.webp`),
    title: 'Registra tu ticket',
    text: 'Registra tu compra de $200 M.N. en productos Heineken en OXXO/WALMART y continúa avanzando hacia la experiencia F1®.'
  },
  SHARED_ONBOARDING_SLIDE
]

export const F1_PHOTO_ONBOARDING_SLIDES: F1OnboardingSlide[] = [
  {
    image: encodeURI(`${F1_PH_ASSETS}/subir_foto-asset_onbording-1.webp`),
    title: 'Comparte tu foto',
    text: 'Sube una foto que capture cómo disfrutas Heineken®.'
  },
  SHARED_ONBOARDING_SLIDE
]

/** True when the given dynamic id belongs to this campaign */
export const isF1Trivia = (id: string | number | undefined) =>
  PIT_STOP_IDS.triviaId !== null && String(id) === String(PIT_STOP_IDS.triviaId)

export const isF1Album = (id: string | number | undefined) =>
  (PIT_STOP_IDS.ticketAlbumId !== null && String(id) === String(PIT_STOP_IDS.ticketAlbumId)) ||
  (PIT_STOP_IDS.photoAlbumId !== null && String(id) === String(PIT_STOP_IDS.photoAlbumId))
